// Keep in sync with: src/lib/olmps/stage-ladder.ts (web) — PURE functions must stay byte-identical.
//
// OLMPS STAGE Ladder Engine — compositional rungs and state tracking.
//
// Pure, deterministic, side-effect-free functions. No DB, no React, no clock
// reads inside the math (the caller passes timestamps/now in). Portable to
// React Native. Exhaustively unit-tested (stage-ladder.test.ts on web).
//
// The governing invariant: rung state reflects genuine COMPOSITION (enough
// lower-rung units inside the time-box), never engagement proxy metrics.
//
// See docs/OLMPS_V9_SPEC.md §4–6 and OLMPS_SYQI_ORACLE_BUILD.md §4b for the
// design contract.

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Default thresholds per rung. */
const DEFAULT_ACQUIRE_THRESHOLD = 4;
const DEFAULT_MAINTAIN_THRESHOLD = 3;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Rung = 'step' | 'today' | 'aim' | 'goal' | 'endeavour';
export type RungState = 'unformed' | 'forming' | 'acquired' | 'maintaining' | 'lapsed';

/** A completion event from telemetry. */
export interface CompletionEvent {
  rung: Rung;
  timestamp: string; // ISO 8601
  qualified: boolean;
}

/** Per-rung configuration. */
export interface RungConfig {
  acquireThreshold?: number; // 4–5, default 4
  maintainThreshold?: number; // 3–5, default 3
}

/** All-rung configuration. */
export interface LadderConfig {
  step?: RungConfig;
  today?: RungConfig;
  aim?: RungConfig;
  goal?: RungConfig;
  endeavour?: RungConfig;
}

/** A time-boxed window. */
export interface Window {
  rung: Rung;
  startTime: string; // ISO 8601 (calendar start, local time)
  endTime: string; // ISO 8601 (calendar end, local time)
  events: CompletionEvent[];
  count: number; // qualified event count
}

/** Live pace projection for the current open window. */
export interface CurrentWindowProjection {
  count: number; // qualified units so far
  needed: number; // threshold to hit
  onTrack: boolean; // true if projected to meet threshold
  projected: number; // expected final count at window end
  elapsedFraction: number; // 0..1, fraction of window elapsed
}

/** State and projection for a single rung. */
export interface RungSnapshot {
  rung: Rung;
  state: RungState;
  /** History of recently closed windows (newest last). */
  history: Array<{
    startTime: string;
    endTime: string;
    count: number;
    state: RungState;
  }>;
  /** Projection for the current open window. */
  currentWindow: CurrentWindowProjection;
}

/** The complete ladder state. */
export interface LadderState {
  snapshot: string; // ISO 8601
  rungs: Record<Rung, RungSnapshot>;
}

// ---------------------------------------------------------------------------
// Helpers: time and windows
// ---------------------------------------------------------------------------

/**
 * Parse an ISO 8601 string into a Date, treating it as local time.
 * Handles both "2026-07-18T14:30:00Z" and "2026-07-18T14:30:00" formats.
 */
function parseIsoLocal(isoStr: string): Date {
  // Remove 'Z' suffix if present, then parse as local time
  const normalized = isoStr.replace('Z', '');
  // Split into date and time components
  const [datePart, timePart] = normalized.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour = 0, minute = 0, second = 0] = (timePart || '00:00:00').split(':').map(Number);

  // Create a local date (not UTC)
  return new Date(year, month - 1, day, hour, minute, Math.floor(second));
}

/**
 * Format a local Date as an ISO string WITHOUT timezone offset.
 * Returns "2026-07-18T14:30:00.000Z" where Z indicates local time.
 */
function formatIsoLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  const second = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  return `${year}-${month}-${day}T${hour}:${minute}:${second}.${ms}Z`;
}

/**
 * ISO 8601 week number (1–53) and year for a date (Monday start).
 * Returns [year, week].
 */
function getIsoWeek(date: Date): [number, number] {
  const d = new Date(date);
  // Adjust to nearest Thursday, then get its year.
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNum = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return [d.getFullYear(), weekNum];
}

/**
 * ISO 8601 week start (Monday 00:00:00 local) for a date.
 */
function isoWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay() || 7; // Sunday = 7
  d.setDate(d.getDate() - (day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * ISO 8601 week end (Sunday 23:59:59 local) for a date.
 */
function isoWeekEnd(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() + (7 - day));
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Month start (1st 00:00:00 local).
 */
function monthStart(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  return d;
}

/**
 * Month end (last day 23:59:59 local).
 */
function monthEnd(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
  return d;
}

/**
 * Quarter start (Q1=Jan 1, Q2=Apr 1, etc., 00:00:00 local).
 */
function quarterStart(date: Date): Date {
  const q = Math.floor(date.getMonth() / 3);
  const d = new Date(date.getFullYear(), q * 3, 1, 0, 0, 0, 0);
  return d;
}

/**
 * Quarter end (90 days after start, 23:59:59 local).
 */
function quarterEnd(date: Date): Date {
  const q = Math.floor(date.getMonth() / 3);
  const d = new Date(date.getFullYear(), q * 3 + 3, 0, 23, 59, 59, 999);
  return d;
}

/**
 * Hour start (MM:00:00 local).
 */
function hourStart(date: Date): Date {
  const d = new Date(date);
  d.setMinutes(0, 0, 0);
  return d;
}

/**
 * Hour end (MM:59:59 local).
 */
function hourEnd(date: Date): Date {
  const d = new Date(date);
  d.setMinutes(59, 59, 999);
  return d;
}

/**
 * Day start (00:00:00 local).
 */
function dayStart(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Day end (23:59:59 local).
 */
function dayEnd(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Get the start and end times for a window of a given rung, at a date.
 * Returns [startKey, endKey] as date strings for grouping (YYYY-MM-DD or similar).
 * Also computes the actual Date objects for use in time calculations.
 */
function getWindowBounds(rung: Rung, date: Date): { key: string; start: Date; end: Date } {
  let start: Date, end: Date, key: string;
  switch (rung) {
    case 'step':
      start = hourStart(date);
      end = hourEnd(date);
      key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}T${String(start.getHours()).padStart(2, '0')}`;
      break;
    case 'today':
      start = dayStart(date);
      end = dayEnd(date);
      key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
      break;
    case 'aim': {
      start = isoWeekStart(date);
      end = isoWeekEnd(date);
      const [year, week] = getIsoWeek(start);
      key = `${year}-W${String(week).padStart(2, '0')}`;
      break;
    }
    case 'goal':
      start = monthStart(date);
      end = monthEnd(date);
      key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
      break;
    case 'endeavour': {
      start = quarterStart(date);
      end = quarterEnd(date);
      const q = Math.floor(start.getMonth() / 3) + 1;
      key = `${start.getFullYear()}-Q${q}`;
      break;
    }
  }
  return { key, start, end };
}

/**
 * Check if a timestamp falls within a window (inclusive on both ends).
 */
function isInWindow(timestamp: string, startTime: string, endTime: string): boolean {
  const t = parseIsoLocal(timestamp).getTime();
  const s = parseIsoLocal(startTime).getTime();
  const e = parseIsoLocal(endTime).getTime();
  return t >= s && t <= e;
}

// ---------------------------------------------------------------------------
// Rung-level composition and state
// ---------------------------------------------------------------------------

/**
 * Resolve config defaults.
 */
function resolveConfig(cfg?: RungConfig): { acquireThreshold: number; maintainThreshold: number } {
  return {
    acquireThreshold: cfg?.acquireThreshold ?? DEFAULT_ACQUIRE_THRESHOLD,
    maintainThreshold: cfg?.maintainThreshold ?? DEFAULT_MAINTAIN_THRESHOLD,
  };
}

/**
 * For a given rung, compute the all-time windows and their counts from
 * events. A window is "formed" if it has >= 1 qualified lower-rung unit,
 * and is "ordered" if it has >= threshold units.
 *
 * Returns windows sorted chronologically (oldest first).
 */
export function computeRungWindows(
  events: CompletionEvent[],
  rung: Rung,
  _config: RungConfig = {},
  now: string,
): Window[] {
  // For the 'step' rung (base case), count raw qualified actions.
  // For higher rungs, filter events to only the next-lower rung.
  const lowerRung = rung === 'step' ? 'step' : getLowerRung(rung);
  const relevantEvents = events.filter((e) => e.rung === lowerRung && e.qualified);

  // Collect all unique windows from the events.
  const windowMap = new Map<string, { bounds: { key: string; start: Date; end: Date }; events: CompletionEvent[]; count: number }>();
  const nowDate = parseIsoLocal(now);

  relevantEvents.forEach((event) => {
    const date = parseIsoLocal(event.timestamp);
    const bounds = getWindowBounds(rung, date);

    if (!windowMap.has(bounds.key)) {
      windowMap.set(bounds.key, {
        bounds,
        events: [],
        count: 0,
      });
    }

    const win = windowMap.get(bounds.key)!;
    win.events.push(event);
    win.count++;
  });

  // Always include the current open window (even if empty).
  const currentBounds = getWindowBounds(rung, nowDate);
  if (!windowMap.has(currentBounds.key)) {
    windowMap.set(currentBounds.key, {
      bounds: currentBounds,
      events: [],
      count: 0,
    });
  }

  // Sort by start time.
  const windows = Array.from(windowMap.values())
    .map((w) => ({
      rung,
      startTime: formatIsoLocal(w.bounds.start),
      endTime: formatIsoLocal(w.bounds.end),
      events: w.events,
      count: w.count,
    }))
    .sort((a, b) => {
      return parseIsoLocal(a.startTime).getTime() - parseIsoLocal(b.startTime).getTime();
    });

  return windows;
}

/**
 * Get the rung immediately below a given rung in the composition ladder.
 */
function getLowerRung(rung: Rung): Rung {
  const order: Rung[] = ['step', 'today', 'aim', 'goal', 'endeavour'];
  const idx = order.indexOf(rung);
  if (idx <= 0) return 'step';
  return order[idx - 1];
}

/**
 * Compute the state for a single rung based on its windows and config.
 * State machine:
 * - unformed: no windows or all below threshold
 * - forming: current open window has events but below acquire threshold
 * - acquired: a closed window reached acquire threshold (first time)
 * - maintaining: after acquired, current closed window >= maintain threshold
 * - lapsed: after acquired, current closed window < maintain threshold
 */
export function computeRungState(
  windows: Window[],
  rung: Rung,
  config: RungConfig = {},
  now: string,
): {
  state: RungState;
  acquiredAtWindow?: string; // ISO of first acquired window start
  history: Array<{ startTime: string; endTime: string; count: number; state: RungState }>;
  currentWindow: CurrentWindowProjection;
} {
  const cfg = resolveConfig(config);
  const nowDate = parseIsoLocal(now);

  if (windows.length === 0) {
    return {
      state: 'unformed',
      history: [],
      currentWindow: {
        count: 0,
        needed: cfg.acquireThreshold,
        onTrack: false,
        projected: 0,
        elapsedFraction: 0,
      },
    };
  }

  // Split into closed windows (before now) and current open window (containing now).
  const closedWindows: Window[] = [];
  let currentWindow: Window | null = null;
  for (const w of windows) {
    const end = parseIsoLocal(w.endTime).getTime();
    if (end < nowDate.getTime()) {
      closedWindows.push(w);
    } else {
      currentWindow = w;
    }
  }

  // Determine if any closed window ever reached acquire threshold.
  let acquiredAtWindow: string | undefined;
  let acquiredWindowIndex = -1;
  closedWindows.forEach((w, idx) => {
    if (w.count >= cfg.acquireThreshold && acquiredWindowIndex === -1) {
      acquiredAtWindow = w.startTime;
      acquiredWindowIndex = idx;
    }
  });

  // Compute state based on acquisition history and transitions.
  let state: RungState = 'unformed';

  if (acquiredWindowIndex === -1) {
    // Never acquired in closed windows. Check current open window and history.
    if (currentWindow && currentWindow.count >= cfg.acquireThreshold) {
      // Current open window is at/above acquire threshold
      state = 'acquired';
    } else if (closedWindows.length === 0 && currentWindow && currentWindow.count > 0) {
      // Current open window has events but below threshold, and no closed windows
      state = 'forming';
    } else if (closedWindows.some((w) => w.count > 0)) {
      // Some closed windows had events but none reached threshold
      state = 'forming';
    } else {
      // No windows with events
      state = 'unformed';
    }
  } else {
    // Has acquired at some point.
    if (acquiredWindowIndex === closedWindows.length - 1) {
      // Most recent closed window is the first (and only) acquired one.
      state = 'acquired';
    } else {
      // There are closed windows after the first acquisition.
      // Check if we've ever fallen below maintain threshold (lapsed).
      let hasLapsed = false;
      for (let i = acquiredWindowIndex + 1; i < closedWindows.length; i++) {
        if (closedWindows[i].count < cfg.maintainThreshold) {
          hasLapsed = true;
          break;
        }
      }

      const mostRecent = closedWindows[closedWindows.length - 1];

      if (hasLapsed) {
        // We've lapsed. If most recent is >= acquire, we've reacquired.
        if (mostRecent.count >= cfg.acquireThreshold) {
          state = 'acquired';
        } else {
          state = 'lapsed';
        }
      } else {
        // No lapse yet. All windows since acquisition are >= maintain.
        if (mostRecent.count >= cfg.maintainThreshold) {
          state = 'maintaining';
        } else {
          // This shouldn't happen (means a window dropped below maintain but we didn't detect lapse above)
          state = 'lapsed';
        }
      }
    }
  }

  // Projection for current window.
  const projection = computeProjection(currentWindow, cfg.acquireThreshold, nowDate);

  // History: last N closed windows (up to 10).
  const history = closedWindows.slice(-10).map((w) => {
    const wState = w.count >= cfg.acquireThreshold ? 'acquired' : w.count > 0 ? 'forming' : 'unformed';
    return {
      startTime: w.startTime,
      endTime: w.endTime,
      count: w.count,
      state: wState as RungState,
    };
  });

  return {
    state,
    acquiredAtWindow,
    history,
    currentWindow: projection,
  };
}

/**
 * Compute live pace projection for the current open window.
 */
function computeProjection(
  currentWindow: Window | null,
  threshold: number,
  now: Date,
): CurrentWindowProjection {
  if (!currentWindow) {
    return {
      count: 0,
      needed: threshold,
      onTrack: false,
      projected: 0,
      elapsedFraction: 0,
    };
  }

  const start = parseIsoLocal(currentWindow.startTime).getTime();
  const end = parseIsoLocal(currentWindow.endTime).getTime();
  const nowTime = now.getTime();

  const elapsedFraction = Math.max(0, Math.min(1, (nowTime - start) / (end - start)));
  const count = currentWindow.count;

  // Simple linear projection: if we maintain this rate, what's the final count?
  const projected = elapsedFraction > 0 ? Math.round(count / elapsedFraction) : count;
  const onTrack = projected >= threshold;

  return {
    count,
    needed: threshold,
    onTrack,
    projected,
    elapsedFraction,
  };
}

// ---------------------------------------------------------------------------
// Top-level ladder computation
// ---------------------------------------------------------------------------

/**
 * Compute the complete ladder state across all rungs.
 */
export function computeLadder(
  events: CompletionEvent[],
  config: LadderConfig = {},
  now: string,
): LadderState {
  const rungs: Rung[] = ['step', 'today', 'aim', 'goal', 'endeavour'];
  const rungSnapshots: Record<Rung, RungSnapshot> = {} as any;

  rungs.forEach((rung) => {
    const windows = computeRungWindows(events, rung, config[rung], now);
    const stateResult = computeRungState(windows, rung, config[rung], now);

    rungSnapshots[rung] = {
      rung,
      state: stateResult.state,
      history: stateResult.history,
      currentWindow: stateResult.currentWindow,
    };
  });

  return {
    snapshot: now,
    rungs: rungSnapshots,
  };
}
